/**
 * Named Table Views endpoints for liveOverviewApi.
 * ClickUp-style view management: list, create, rename, delete, set default.
 */
import { supabase } from "../../../../lib/supabase";
import { liveOverviewApi } from "./liveOverviewApi";
import { ALLE_KLASSEN_SENTINEL } from "../../klassenlisten/utils/alleKlassenScope";

const tableViewsApi = liveOverviewApi.injectEndpoints({
  endpoints: (builder) => ({
    listTableViews: builder.query<
      Array<{
        id: string;
        view_name: string;
        visibility: "private" | "shared";
        user_profile_id: string;
        class_id: string | null;
        column_order: string[];
        column_visibility: Record<string, boolean>;
        column_widths: Record<string, number>;
        sort_field: string | null;
        sort_direction: string;
        group_by: string | null;
        filters: any[] | null;
        shared_with_users: string[] | null;
        shared_with_groups: string[] | null;
        updated_at: string;
      }>,
      { classId: string | null; userId: string | null }
    >({
      async queryFn({ classId, userId }) {
        try {
          if (!userId) return { data: [] as any[] };

          let query = supabase
            .from("user_table_preferences" as never)
            .select(
              "id, view_name, visibility, user_profile_id, class_id, column_order, column_visibility, column_widths, sort_field, sort_direction, group_by, filters, shared_with_users, shared_with_groups, updated_at",
            )
            .eq("table_key" as never, "klassenliste" as never)
            .order("updated_at" as never, { ascending: false });

          // Only show own views + shared views from others
          if (userId) {
            query = query.or(
              `user_profile_id.eq.${userId},visibility.eq.shared` as never,
            );
          }

          if (classId) {
            // For a specific class: show class-specific + Alle Klassen views + global views
            query = query.or(
              `class_id.eq.${classId},class_id.eq.${ALLE_KLASSEN_SENTINEL},class_id.is.null` as never,
            );
          } else if (classId === ALLE_KLASSEN_SENTINEL) {
            // Alle Klassen: show sentinel-scoped + global views
            query = query.or(
              `class_id.eq.${ALLE_KLASSEN_SENTINEL},class_id.is.null` as never,
            );
          } else {
            query = query.is("class_id" as never, null as never);
          }

          const { data, error } = await query;
          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }
          return { data: (data || []) as any[] };
        } catch (e) {
          return { error: { status: "CUSTOM_ERROR", error: String(e) } };
        }
      },
      providesTags: (_r, _e, { classId }) => [
        { type: "TableViews", id: classId || "global" },
      ],
      keepUnusedDataFor: 300,
    }),

    createTableView: builder.mutation<
      { id: string },
      {
        classId: string | null;
        viewName: string;
        visibility: "private" | "shared";
        columnOrder?: string[];
        columnVisibility?: Record<string, boolean>;
        columnWidths?: Record<string, number>;
        sortField?: string | null;
        sortDirection?: string;
        groupBy?: string | null;
        filters?: any[] | null;
        courseIds?: string[] | null;
        sharedWithUsers?: string[] | null;
        sharedWithGroups?: string[] | null;
      }
    >({
      async queryFn({
        classId,
        viewName,
        visibility,
        columnOrder,
        columnVisibility,
        columnWidths,
        sortField,
        sortDirection,
        groupBy,
        filters,
        courseIds,
        sharedWithUsers,
        sharedWithGroups,
      }) {
        try {
          const { data: userRes } = await supabase.auth.getUser();
          const userId =
            userRes?.user?.user_metadata?.profile_id || userRes?.user?.id;
          const { data: schoolData } = await supabase.rpc(
            "get_user_school_id" as never,
          );
          const schoolId = schoolData as string;

          if (!userId || !schoolId) {
            return {
              error: { status: "CUSTOM_ERROR", error: "No auth context" },
            };
          }

          const row = {
            user_profile_id: userId,
            school_id: schoolId,
            table_key: "klassenliste",
            class_id: classId,
            view_name: viewName,
            visibility,
            column_order: columnOrder || [],
            column_visibility: columnVisibility || {},
            column_widths: columnWidths || {},
            sort_field: sortField || null,
            sort_direction: sortDirection || "asc",
            group_by: groupBy || null,
            filters: filters || null,
            course_ids: courseIds || null,
            shared_with_users: sharedWithUsers || null,
            shared_with_groups: sharedWithGroups || null,
          };

          const { data, error } = await supabase
            .from("user_table_preferences" as never)
            .insert(row as never)
            .select("id" as never)
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }
          return { data: { id: (data as any).id } };
        } catch (e) {
          return { error: { status: "CUSTOM_ERROR", error: String(e) } };
        }
      },
      invalidatesTags: ["TableViews", "InitData"],
    }),

    updateTableView: builder.mutation<
      void,
      {
        viewId: string;
        viewName?: string;
        visibility?: "private" | "shared";
        sharedWithUsers?: string[] | null;
        sharedWithGroups?: string[] | null;
      }
    >({
      async queryFn({
        viewId,
        viewName,
        visibility,
        sharedWithUsers,
        sharedWithGroups,
      }) {
        try {
          const patch: Record<string, unknown> = {
            updated_at: new Date().toISOString(),
          };
          if (viewName !== undefined) patch.view_name = viewName;
          if (visibility !== undefined) patch.visibility = visibility;
          if (sharedWithUsers !== undefined)
            patch.shared_with_users = sharedWithUsers;
          if (sharedWithGroups !== undefined)
            patch.shared_with_groups = sharedWithGroups;

          const { error } = await supabase
            .from("user_table_preferences" as never)
            .update(patch as never)
            .eq("id" as never, viewId as never);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }
          return { data: undefined };
        } catch (e) {
          return { error: { status: "CUSTOM_ERROR", error: String(e) } };
        }
      },
      invalidatesTags: ["TableViews", "InitData"],
    }),

    deleteTableView: builder.mutation<void, { viewId: string }>({
      async queryFn({ viewId }) {
        try {
          const { error } = await supabase
            .from("user_table_preferences" as never)
            .delete()
            .eq("id" as never, viewId as never);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }
          return { data: undefined };
        } catch (e) {
          return { error: { status: "CUSTOM_ERROR", error: String(e) } };
        }
      },
      invalidatesTags: ["TableViews", "TablePrefs", "InitData"],
    }),

    hideSharedViewForMe: builder.mutation<void, { viewId: string }>({
      async queryFn({ viewId }) {
        try {
          const { data, error } = await supabase.rpc(
            "fn_klassenliste_hide_view_for_me" as never,
            { p_view_id: viewId } as never,
          );
          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }
          if (data !== true) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: "View could not be removed for current user",
              },
            };
          }
          return { data: undefined };
        } catch (e) {
          return { error: { status: "CUSTOM_ERROR", error: String(e) } };
        }
      },
      invalidatesTags: ["TableViews", "InitData"],
    }),

    saveViewColumns: builder.mutation<
      void,
      {
        viewId: string;
        classId?: string | null;
        columnOrder?: string[];
        columnVisibility?: Record<string, boolean>;
        columnWidths?: Record<string, number>;
        sortField?: string | null;
        sortDirection?: string;
        groupBy?: string | null;
        filters?: any[] | null;
        courseIds?: string[] | null;
      }
    >({
      async queryFn({
        viewId,
        classId,
        columnOrder,
        columnVisibility,
        columnWidths,
        sortField,
        sortDirection,
        groupBy,
        filters,
        courseIds,
      }) {
        try {
          // Virtual Standard view → use the upsert RPC (single call)
          if (viewId === '__virtual_standard__') {
            const params: Record<string, unknown> = {
              p_view_name: 'Standard',
              p_class_id: classId || null,
            };
            if (columnOrder !== undefined) params.p_column_order = columnOrder;
            if (columnVisibility !== undefined) params.p_column_visibility = columnVisibility;
            if (columnWidths !== undefined) params.p_column_widths = columnWidths;
            if (sortField !== undefined) params.p_sort_field = sortField;
            if (sortDirection !== undefined) params.p_sort_direction = sortDirection;
            if (groupBy !== undefined) params.p_group_by = groupBy;
            if (filters !== undefined) params.p_filters = filters;
            if (courseIds !== undefined) params.p_course_ids = courseIds;

            const { error } = await supabase.rpc('fn_klassenliste_save_preferences' as never, params as never);
            if (error) {
              return { error: { status: 'CUSTOM_ERROR', error: error.message } };
            }
            return { data: undefined };
          }

          // Real view ID → direct update
          const patch: Record<string, unknown> = {
            updated_at: new Date().toISOString(),
          };
          if (columnOrder !== undefined) patch.column_order = columnOrder;
          if (columnVisibility !== undefined) patch.column_visibility = columnVisibility;
          if (columnWidths !== undefined) patch.column_widths = columnWidths;
          if (sortField !== undefined) patch.sort_field = sortField;
          if (sortDirection !== undefined) patch.sort_direction = sortDirection;
          if (groupBy !== undefined) patch.group_by = groupBy;
          if (filters !== undefined) patch.filters = filters;
          if (courseIds !== undefined) patch.course_ids = courseIds;

          const { error } = await supabase
            .from("user_table_preferences" as never)
            .update(patch as never)
            .eq("id" as never, viewId as never);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }
          return { data: undefined };
        } catch (e) {
          return { error: { status: "CUSTOM_ERROR", error: String(e) } };
        }
      },
      // Invalidate InitData so sortedViews has fresh column state when switching views
      invalidatesTags: ["InitData"],
    }),

    listUserGroups: builder.query<
      Array<{ id: string; name: string; type: string; member_count: number }>,
      void
    >({
      async queryFn() {
        try {
          const { data, error } = await supabase
            .from("user_groups" as never)
            .select("id, name, type, user_group_members(count)" as never)
            .eq("is_active" as never, true as never)
            .order("name" as never);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          const groups = ((data || []) as any[]).map((g: any) => ({
            id: g.id,
            name: g.name,
            type: g.type,
            member_count: g.user_group_members?.[0]?.count ?? 0,
          }));

          return { data: groups };
        } catch (e) {
          return { error: { status: "CUSTOM_ERROR", error: String(e) } };
        }
      },
      keepUnusedDataFor: 600,
    }),

    listStaffMembers: builder.query<
      Array<{
        id: string;
        first_name: string;
        last_name: string;
        role_name: string;
      }>,
      { schoolId?: string } | void
    >({
      async queryFn(arg) {
        try {
          let schoolId = arg && typeof arg === 'object' ? arg.schoolId : undefined;
          if (!schoolId) {
            const { data: schoolData } = await supabase.rpc(
              "get_user_school_id" as never,
            );
            schoolId = schoolData as string;
          }

          if (!schoolId) return { data: [] };

          const { data, error } = await supabase
            .from("vw_react_staff_management" as never)
            .select("id, first_name, last_name, role_name" as never)
            .eq("school_id" as never, schoolId as never)
            .eq("is_external" as never, false as never)
            .order("last_name" as never, { ascending: true })
            .order("first_name" as never, { ascending: true });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          const members = (data || []).map((row: any) => ({
            id: row.id,
            first_name: row.first_name || "",
            last_name: row.last_name || "",
            role_name: row.role_name || "",
          }));

          return { data: members };
        } catch (e) {
          return { error: { status: "CUSTOM_ERROR", error: String(e) } };
        }
      },
      keepUnusedDataFor: 600,
    }),
  }),
  overrideExisting: false,
});

export const {
  useListTableViewsQuery,
  useCreateTableViewMutation,
  useUpdateTableViewMutation,
  useDeleteTableViewMutation,
  useHideSharedViewForMeMutation,
  useSaveViewColumnsMutation,
  useListUserGroupsQuery,
  useListStaffMembersQuery,
} = tableViewsApi;
